import {computed, inject, Injectable, OnDestroy, signal} from '@angular/core';
import {Subscription} from 'rxjs';
import {firstValueFrom} from 'rxjs';
import {FiscalJobHttpService} from './fiscal-job-http.service';
import {FiscalJobHubClient} from './fiscal-job-hub-client';
import {
  FiscalJobCreateRequest,
  FiscalJobDetail,
  FiscalJobKind,
  FiscalJobSummary,
  isFiscalJobFinished,
  sortFiscalJobs
} from '../../_models/fiscal-jobs/fiscal-job.models';

/**
 * How long after the last hub push the store keeps a fallback poll running for an unfinished run.
 *
 * Only armed while the hub is **down** — at open/create time *or* the moment it drops afterwards (Bug #784).
 * With a socket there is nothing to poll for; without one, a run that finishes in the background would
 * otherwise sit on screen saying "running" until the operator reloaded — and the operator's next action is
 * usually to wait for exactly that. Fifteen seconds is short enough to feel live and long enough that a tab
 * left open on a finished list costs nothing. The poll stops on three conditions, all of which it checks
 * itself: the run finished, nothing is open, or the socket came back.
 */
export const FISCAL_JOB_POLL_INTERVAL_MS = 15_000;

/**
 * The SPA's view of the fiscal job engine (#768) — a signal store over
 * {@link FiscalJobHttpService}, refreshed by the progress hub and readable without it.
 *
 * ### The push is a notification; this is the state
 *
 * A hub message says "run X advanced" and nothing more. This store's reaction is always the same: refetch the
 * run. That asymmetry is the whole design and it is what makes three things true at once — a reload shows the
 * same run, a second tab agrees with the first, and a finished export stays listed tomorrow for re-download.
 * A store that trusted the payload would have a second, weaker copy of the record that disagrees with it
 * whenever a message is dropped, reordered, or delivered to a tab that reconnected mid-run.
 *
 * ### Readable with no connection at all
 *
 * Every path here works without the hub: {@link refreshList} and {@link open} are plain requests, every
 * mutating call refetches its own result, and while the socket is down an unfinished run is polled on a timer
 * ({@link FISCAL_JOB_POLL_INTERVAL_MS}). The hub only removes latency. That is the same
 * push-first/safety-net-last arrangement `GateRefreshService` uses, and for the same reason: the feature must
 * not depend on a socket that a proxy, a corporate firewall or an older server may simply not give us.
 *
 * ### Ownership
 *
 * This store is the **single owner** of {@link FiscalJobHubClient}: it starts the hub and stops it when the
 * last screen using it leaves, so there is never a question of which of the two closes the socket. A
 * component injects this, calls {@link start} in `ngOnInit` and {@link stop} in `ngOnDestroy` — both halves,
 * because the store is `providedIn: 'root'` and therefore outlives every screen (Bug #783).
 *
 * ### Responses are applied in request order, and the list's order is the data's
 *
 * One push fires two reads, so "whichever answers last wins" is not good enough: the slower answer is often
 * the older one. Every read stamps itself when it is **issued** and a response is discarded if a later one
 * has already written what it would write (Bug #789). And the list is sorted on every write
 * ({@link sortFiscalJobs}) rather than assembled by prepending, so a run keeps its place when it is opened,
 * created or retried — the row order says when the runs were made, not what the reader last touched.
 *
 * ### No components here, on purpose
 *
 * Story #768 ships the machinery; the screens that sit on it are #769 (the TSE overview), #770 (data exports)
 * and #771 (mass re-filing). Everything a screen needs is a signal on this class.
 */
@Injectable({providedIn: 'root'})
export class FiscalJobStore implements OnDestroy {
  private readonly http = inject(FiscalJobHttpService);
  private readonly hub = inject(FiscalJobHubClient);

  /** The tenant's runs, newest first. Empty until {@link refreshList} has answered once. */
  readonly jobs = signal<FiscalJobSummary[]>([]);

  /** The run currently opened, with its entries, or null. */
  readonly openJob = signal<FiscalJobDetail | null>(null);

  /** True while a list request is in flight. */
  readonly listLoading = signal(false);

  /** True while the opened run is being (re)read. */
  readonly jobLoading = signal(false);

  /**
   * The last error code a call answered (an `E_FISCAL_JOB_*` code), or null.
   *
   * A CODE, never a message: the description belongs to the catalog and to the screen's own wording, and a
   * store that kept server prose would be the one place a string nobody reviewed could reach a UI.
   */
  readonly lastError = signal<string | null>(null);

  /** Whether the progress hub is connected. A screen may show a quieter "live" hint; nothing depends on it. */
  readonly live = this.hub.connected.asReadonly();

  /** The opened run's entries that could be retried right now. */
  readonly retryableItems = computed(() => this.openJob()?.items.filter(i => i.retryable) ?? []);

  /** Whether the opened run still has work outstanding. */
  readonly openJobRunning = computed(() => {
    const job = this.openJob();
    return job !== null && !isFiscalJobFinished(job.job.state);
  });

  private hubSubscription: Subscription | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * A monotonic stamp taken when a read is **issued**, so responses are applied in request order rather than
   * arrival order (Bug #789).
   *
   * One hub push fires `refreshList()` and `open()` together, and whichever the network happens to answer
   * last used to win. That is last-response-wins, and it is a lie whenever the slower response is also the
   * older one — a run that had just finished would flicker back to „Wird erstellt" because a list read issued
   * beforehand landed afterwards.
   */
  private requestSeq = 0;

  /** The issue stamp of the whole-list response currently on screen. */
  private appliedListSeq = 0;

  /**
   * The issue stamp of the newest response that wrote each row, by run id.
   *
   * A row is only overwritten by a response that was issued **later** than the one that wrote it, whether
   * that response was a list read or a single run's. Cleared with the store, because it is per screen-session
   * and would otherwise grow for the life of the tab.
   */
  private readonly rowSeq = new Map<string, number>();

  /**
   * How many screens have called {@link start} and not yet {@link stop}.
   *
   * A count rather than a boolean, because this store is `providedIn: 'root'` and #769/#771 are two more
   * screens on the same engine. With a boolean the first screen to leave would close the socket under a
   * sibling that is still open, which is the *other* half of the bug #783 fixes.
   */
  private starters = 0;

  /**
   * Begin following the progress hub. Idempotent per caller.
   *
   * Called by every screen that shows a run, and matched by a {@link stop} in that screen's `ngOnDestroy`.
   * Angular DI is lazy, so a store nothing injects never runs — the same arrangement `GateRefreshService`
   * documents.
   */
  start(): void {
    if (++this.starters > 1) return;

    this.hub.start();
    this.hubSubscription = this.hub.signals.subscribe(signal => {
      if (signal.kind === 'connected') {
        // A push raised while the socket was down was never delivered (SignalR does not replay), so
        // "connected" and "reconnected" oblige us to do the same thing: catch up once. The poll stops here
        // rather than waiting for its own next tick: a poll running beside a healthy socket is pure load.
        void this.catchUp();
        this.stopPolling();
        return;
      }

      if (signal.kind === 'disconnected') {
        // Bug #784. The fallback used to be armed only if the hub was ALREADY down when a run was opened or
        // created, so the ordinary case — create a run on a healthy socket, then lose the connection — left
        // the screen with neither a push nor a poll, showing „Wird erstellt" until a reload. SignalR's
        // `withAutomaticReconnect()` gives up permanently after its default four attempts, so "it will come
        // back on its own" is not a guarantee we may rely on.
        this.armPollingIfNeeded();
        return;
      }

      // A push about a run nobody is looking at still moves the list row's counts, so the list is refreshed
      // either way; the opened run is refetched only when the push is about it.
      void this.refreshList();
      if (this.openJob()?.job.id === signal.jobId) void this.open(signal.jobId);
    });
  }

  /**
   * Release this store on behalf of one {@link start} (Bug #783).
   *
   * The last caller to leave takes the subscription, the fallback poll, the opened run and the hub with it.
   * Without this the store outlived every screen — it is root-provided, so its own `ngOnDestroy` runs at
   * application teardown — and went on polling `/fiscal/jobs/Get` for a run nobody could see. Extra calls
   * are harmless.
   */
  stop(): void {
    if (this.starters === 0) return;
    if (--this.starters > 0) return;
    this.release();
  }

  ngOnDestroy(): void {
    this.starters = 0;
    this.release();
  }

  /** Tear everything down. The one place that does, so {@link stop} and {@link ngOnDestroy} cannot drift. */
  private release(): void {
    this.hubSubscription?.unsubscribe();
    this.hubSubscription = null;
    this.stopPolling();
    this.rowSeq.clear();
    // The opened run is forgotten too: it belonged to a screen that is gone, and leaving it set would make
    // the next visit render a stale run before its own read answers.
    this.openJob.set(null);
    this.hub.stop();
  }

  /** Reads the tenant's runs. */
  async refreshList(kind?: FiscalJobKind, take = 50, skip = 0): Promise<void> {
    const seq = ++this.requestSeq;
    this.listLoading.set(true);
    try {
      const summaries = await firstValueFrom(this.http.list(kind, take, skip));
      this.applyList(summaries, seq);
      this.lastError.set(null);
    } catch (error) {
      this.captureError(error);
    } finally {
      this.listLoading.set(false);
    }
  }

  /**
   * Opens a run and reads it with its entries. Also the refetch every push and every mutation answers with —
   * there is deliberately only one read path, so a screen cannot end up showing a value the refresh would
   * disagree with.
   */
  async open(jobId: string): Promise<void> {
    const seq = ++this.requestSeq;
    this.jobLoading.set(true);
    try {
      const detail = await firstValueFrom(this.http.get(jobId));
      this.openJob.set(detail);
      this.mergeIntoList(detail.job, seq);
      this.lastError.set(null);
      this.armPollingIfNeeded();
    } catch (error) {
      this.captureError(error);
    } finally {
      this.jobLoading.set(false);
    }
  }

  /** Forgets the opened run and stops any fallback polling for it. */
  close(): void {
    this.openJob.set(null);
    this.stopPolling();
  }

  /**
   * Starts a run and opens it.
   *
   * @returns the created run, or null when the request was refused — {@link lastError} then holds the code.
   */
  async create(request: FiscalJobCreateRequest): Promise<FiscalJobDetail | null> {
    const seq = ++this.requestSeq;
    this.jobLoading.set(true);
    try {
      const detail = await firstValueFrom(this.http.create(request));
      this.openJob.set(detail);
      this.mergeIntoList(detail.job, seq);
      this.lastError.set(null);
      this.armPollingIfNeeded();
      return detail;
    } catch (error) {
      this.captureError(error);
      return null;
    } finally {
      this.jobLoading.set(false);
    }
  }

  /**
   * Re-runs one entry of the opened run.
   *
   * The server answers with the whole refreshed run, so this needs no second read — and cannot show a state
   * that the next refetch would contradict.
   *
   * @returns true when the retry was accepted.
   */
  async retryItem(jobId: string, itemId: string): Promise<boolean> {
    const seq = ++this.requestSeq;
    this.jobLoading.set(true);
    try {
      const detail = await firstValueFrom(this.http.retryItem(jobId, itemId));
      this.openJob.set(detail);
      this.mergeIntoList(detail.job, seq);
      this.lastError.set(null);
      this.armPollingIfNeeded();
      return true;
    } catch (error) {
      this.captureError(error);
      return false;
    } finally {
      this.jobLoading.set(false);
    }
  }

  /** Re-reads the list and, when one is open, that run — the catch-up a (re)connect triggers. */
  private async catchUp(): Promise<void> {
    await this.refreshList();
    const open = this.openJob();
    if (open) await this.open(open.job.id);
  }

  /**
   * Arms the fallback poll when — and only when — there is unfinished work AND no live connection.
   *
   * With a socket the push does this job and a poll would be pure load. Without one, this is the difference
   * between a screen that finishes by itself and a screen that needs a reload.
   */
  private armPollingIfNeeded(): void {
    this.stopPolling();
    if (!this.openJobRunning() || this.hub.connected()) return;

    this.pollTimer = setInterval(() => {
      const open = this.openJob();
      if (!open || isFiscalJobFinished(open.job.state) || this.hub.connected()) {
        this.stopPolling();
        return;
      }
      void this.open(open.job.id);
    }, FISCAL_JOB_POLL_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollTimer !== null) clearInterval(this.pollTimer);
    this.pollTimer = null;
  }

  /**
   * Applies a whole-list response.
   *
   * Two things a plain `jobs.set(...)` got wrong and both were visible (Bug #789). A list response issued
   * before another one must not be applied after it, or the older answer is what stays on screen. And a row
   * that a **later** single-run read has already refreshed must survive this write, because that read knows
   * more about that one run than a list read issued before it did.
   */
  private applyList(summaries: FiscalJobSummary[], seq: number): void {
    if (seq < this.appliedListSeq) return;
    this.appliedListSeq = seq;

    const current = new Map(this.jobs().map(j => [j.id, j]));
    const applied = summaries.map(summary => {
      const fresher = (this.rowSeq.get(summary.id) ?? 0) > seq;
      if (fresher) return current.get(summary.id) ?? summary;
      this.rowSeq.set(summary.id, seq);
      return summary;
    });

    this.jobs.set(sortFiscalJobs(applied));
  }

  /**
   * Replaces (or inserts) one run's row, so the list and the detail view cannot disagree.
   *
   * **It no longer moves the row** (Bug #789). It used to prepend, and it is called from {@link open} as well
   * as {@link create} and {@link retryItem} — so merely looking at an old export jumped it to the top of a
   * list rendered and documented as newest-first, under the cursor of the person reading it. Position is a
   * property of the data ({@link sortFiscalJobs}), never of what was clicked last.
   */
  private mergeIntoList(summary: FiscalJobSummary, seq: number): void {
    if ((this.rowSeq.get(summary.id) ?? 0) > seq) return;
    this.rowSeq.set(summary.id, seq);

    const rest = this.jobs().filter(j => j.id !== summary.id);
    this.jobs.set(sortFiscalJobs([summary, ...rest]));
  }

  /**
   * Stores the error CODE of a failed call.
   *
   * The structured `ApiError` body carries `{ code, description, statusCode }`; only the code is kept. A 401
   * is deliberately not special-cased here — `ErrorInterceptor` already owns the recover-or-logout decision
   * for a dead session, and a second opinion from a background refetch is how a session gets signed out twice.
   */
  private captureError(error: unknown): void {
    const body = (error as { error?: { code?: unknown } } | null)?.error;
    this.lastError.set(typeof body?.code === 'string' ? body.code : 'E_FISCAL_JOB_REQUEST_FAILED');
  }
}
